import { Account, AccountSession } from "../models/user"

const cacheKey = "hg::cache::user"

// The cache nominally holds an AccountSession, but the post-payment redirect
// flow writes either a full Account or a bare { access_token } into the same
// slot. Reads only ever touch access_token / session_id / expires / is_signed_up.
type CachedUserPayload = AccountSession | Account | { access_token: string }

export type AccountType = "user" | "guest"

// The cached user is the single source of truth for who the session belongs
// to. Components read it through useSyncExternalStore, so every write has to
// notify - otherwise a guest session minted inside the fetch layer would not
// show up in the UI.
const listeners = new Set<() => void>()
const notify = () => listeners.forEach((listener) => listener())

export const subscribeCachedUser = (listener: () => void) => {
  listeners.add(listener)
  // Another tab signing in or re-authing writes the same key; without this the
  // UI here would only catch up on its next own write
  const onStorage = (event: StorageEvent) => {
    if (event.key === cacheKey || event.key === null) {
      listener()
    }
  }
  if (typeof window !== "undefined") {
    window.addEventListener("storage", onStorage)
  }
  return () => {
    listeners.delete(listener)
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", onStorage)
    }
  }
}

export const setCachedUser = (user: CachedUserPayload) => {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return
  }
  localStorage.setItem(cacheKey, JSON.stringify(user))
  notify()
}

export const removeCachedUser = () => {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return
  }
  localStorage.removeItem(cacheKey)
  notify()
}

export const getCachedUser = ():
  | Partial<AccountSession>
  | { access_token: string }
  | Partial<Account>
  | null => {
  if (typeof window === "undefined" || typeof localStorage === "undefined") {
    return null
  }
  const raw = localStorage.getItem(cacheKey)
  return raw
    ? (JSON.parse(raw) as
        | Partial<AccountSession>
        | { access_token: string }
        | Partial<Account>)
    : null
}

// A payload without is_signed_up (the bare { access_token } write) is a guest,
// matching what the old bootstrap effect concluded.
export const getCachedAccountType = (): AccountType => {
  const user = getCachedUser() as { is_signed_up?: boolean } | null
  return user?.is_signed_up ? "user" : "guest"
}

// Fired by the fetch layer when it refuses to replace a session (see On401
// "throw") - whichever call hit the 401: a query, a mutation, or a direct
// api call inside an event handler. Pages subscribe once instead of wiring
// every call site.
export type SessionRefusedKind = AccountType

const sessionRefusedListeners = new Set<(kind: SessionRefusedKind) => void>()

export const subscribeSessionRefused = (
  listener: (kind: SessionRefusedKind) => void
) => {
  sessionRefusedListeners.add(listener)
  return () => {
    sessionRefusedListeners.delete(listener)
  }
}

export const notifySessionRefused = (kind: SessionRefusedKind) =>
  sessionRefusedListeners.forEach((listener) => listener(kind))
